import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, Buildings, Stack, Scroll, ArrowRight, CaretUp, CaretDown } from "@phosphor-icons/react";

function PageHeader({ kicker, title, description, right }) {
  return (
    <header className="flex items-end justify-between gap-6 mb-8">
      <div>
        {kicker && <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">{kicker}</div>}
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
        {description && <p className="text-sm text-slate-600 mt-2 max-w-2xl">{description}</p>}
      </div>
      {right}
    </header>
  );
}

export default function PublicBrowse() {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [manufacturers, setManufacturers] = useState([]);
  const [series, setSeries] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState(initialQuery);
  const [activeMfr, setActiveMfr] = useState("all");
  const [activeSeries, setActiveSeries] = useState("all");
  const [seriesCollapsed, setSeriesCollapsed] = useState(false);

  // If user lands here via /browse?q=..., honour the query
  useEffect(() => {
    const q = searchParams.get("q") || "";
    if (q && q !== search) setSearch(q);
  }, [searchParams, search]);

  useEffect(() => {
    api.get("/manufacturers").then(({ data }) => setManufacturers(data));
    api.get("/series").then(({ data }) => setSeries(data));
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
  }, []);

  useEffect(() => {
    const params = {};
    if (activeMfr !== "all") params.manufacturer_id = activeMfr;
    if (activeSeries !== "all") params.series_id = activeSeries;
    if (search) params.search = search;
    api.get("/products", { params }).then(({ data }) => setProducts(data));
  }, [activeMfr, activeSeries, search]);

  const filteredSeries = activeMfr === "all" ? series : series.filter(s => s.manufacturer_id === activeMfr);
  const mfrMap = Object.fromEntries(manufacturers.map(m => [m.id, m]));
  const seriesMap = Object.fromEntries(series.map(s => [s.id, s]));

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader
        kicker="Inventory Catalogue"
        title="Browse the vinyl catalogue"
        description="Search the live catalogue by manufacturer, series, and color."
      />

      {/* Stat strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 mb-8 border border-slate-200 bg-white" data-testid="public-stats">
          {[
            { label: "Total Meters", value: stats.total_meters?.toFixed(2) },
            { label: "Active Rolls", value: stats.active_rolls },
            { label: "Manufacturers", value: stats.manufacturers_count },
            { label: "Products", value: stats.products_count },
          ].map((s, i) => (
            <div key={i} className={`p-6 ${i !== 3 ? "border-r border-slate-200" : ""}`}>
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">{s.label}</div>
              <div className="font-mono text-3xl font-semibold text-slate-900 mt-2 tabular-nums">{s.value ?? "—"}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="mb-6 flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="browse-search"
            placeholder="Search by brand, series, color or SKU — e.g. 3M 2080 Gloss Black" className="pl-9 h-10 rounded-md bg-white" />
        </div>
      </div>

      {/* Manufacturer filter */}
      <div className="mb-3">
        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Manufacturer</div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setActiveMfr("all"); setActiveSeries("all"); }}
            data-testid="filter-mfr-all"
            className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-all ${
              activeMfr === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400"
            }`}>All</button>
          {manufacturers.map((m) => (
            <button key={m.id}
              onClick={() => { setActiveMfr(m.id); setActiveSeries("all"); }}
              data-testid={`filter-mfr-${m.id}`}
              className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-all ${
                activeMfr === m.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400"
              }`}>{m.name}</button>
          ))}
        </div>
      </div>

      {/* Series filter */}
      {filteredSeries.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Series</div>
            <button onClick={() => setSeriesCollapsed((v) => !v)} data-testid="toggle-series-btn"
              className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.15em] uppercase text-slate-400 hover:text-slate-700 transition-colors">
              {seriesCollapsed ? (<><CaretDown size={11} /> Show ({filteredSeries.length})</>) : (<><CaretUp size={11} /> Hide</>)}
            </button>
            {seriesCollapsed && activeSeries !== "all" && (
              <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-blue-600">· {seriesMap[activeSeries]?.name}</span>
            )}
          </div>
          {!seriesCollapsed && (
            <div className="flex flex-wrap gap-2" data-testid="series-chips">
              <button onClick={() => setActiveSeries("all")}
                className={`px-3 py-1.5 text-xs font-medium border rounded-md ${
                  activeSeries === "all" ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 hover:border-slate-400"
                }`}>All</button>
              {filteredSeries.map((s) => (
                <button key={s.id} onClick={() => setActiveSeries(s.id)}
                  className={`px-3 py-1.5 text-xs font-medium border rounded-md ${
                    activeSeries === s.id ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 hover:border-slate-400"
                  }`}>{s.name}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Product grid */}
      {products.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center">
          <Buildings size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">No products match your filters yet.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-slate-200 bg-white" data-testid="product-grid">
          {products.map((p, idx) => (
            <Link key={p.id} to={`/browse/product/${p.id}`}
              data-testid={`product-card-${p.id}`}
              className={`group p-6 hover:bg-slate-50 transition-colors duration-150 border-slate-200 ${
                idx % 3 !== 2 ? "lg:border-r" : ""
              } ${idx < products.length - (products.length % 3 || 3) ? "lg:border-b" : ""}`}>
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 shrink-0 border border-slate-200 rounded-md"
                  style={{ background: p.color_hex || "#e2e8f0" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">
                    {mfrMap[p.manufacturer_id]?.name} · {seriesMap[p.series_id]?.name}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mt-1 truncate">{p.name}</h3>
                  <div className="font-mono text-xs text-slate-600 mt-1">{p.base_sku}</div>
                </div>
                <ArrowRight size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
